/**
 * Learning hub (LCC / lms-gated) — integrates the LMS and curricula surfaces
 * into one sidebar page with internal tabs.
 * Tabs: My Learning · My Trainings · Courses · Curricula.
 * 'My Trainings' is the staff side of the TRUS-486 training program — what a person
 * owes and has completed, which is distinct from the classroom courses in 'My Learning'.
 * Certifications live in the Training hub only (they were duplicated here).
 */
import React from 'react';
import { useCapability } from '@/hooks/useCapabilities';
import { usePageCapability } from '@/hooks/usePageCapability';
import { PAGE_SWITCH, usePageSwitch } from './usePageSwitch';
import { BookOpen, ClipboardList, GraduationCap, Layers, Users } from 'lucide-react';
import HubShell from './HubShell';
import StaffLearnPage from '../learn/page';
import LmsCoursesAdminPage from '../courses/page';
import WorkforceCurricula from '../curricula/page';
import MyTrainingsPage from '../training-program/MyTrainingsPage';
import MyTeamPage from '../training-program/MyTeamPage';

export const LEARNING_HUB_PAGE_KEY = 'workforce-learning-hub';

/** Page capability that lets an org turn My Team off per role, in Settings → Page
 *  Access (backend migration 202610161300). */
export const MY_TEAM_PAGE_CAPABILITY = 'workforce.learning_my_team';

export default function LearningHubPage() {
  // Gated on `staff_training`, not `lms` — see the note in TrainingHubPage.
  const orgId = Number(localStorage.getItem('active_cbo_id') || 0) || null;
  const { enabled: staffTraining } = useCapability(orgId, 'staff_training');
  // TRUS-562: the classroom tabs (My Learning, Courses, Curricula) belong to the LMS
  // and follow `lms`. LCC holds it and sees no change; an org running only the staff
  // training program (Amigos, once `lms` is off) gets My Trainings and My Team alone.
  const { enabled: lms } = useCapability(orgId, 'lms');
  // Resolved for the signed-in person's role. Hidden only once the check says this
  // role is switched off. 'page_not_found' (capability not registered yet, or the
  // check failed) keeps today's behaviour rather than hiding My Team for everyone.
  const myTeamAccess = usePageCapability(orgId, MY_TEAM_PAGE_CAPABILITY);
  const showMyTeam =
    !myTeamAccess.loading && (myTeamAccess.hasAccess || myTeamAccess.accessSource === 'page_not_found');
  // TRUS-562: My Trainings can be switched off per role in the Permission Matrix too.
  const showMyTrainings = !usePageSwitch(orgId, PAGE_SWITCH.myTrainings).switchedOff;

  return (
    <HubShell
      pageKey={LEARNING_HUB_PAGE_KEY}
      title="Learning"
      subtitle={
        lms
          ? 'Classroom learning, curricula, and the certifications they lead to.'
          : 'Your trainings, and your team\u2019s.'
      }
      tabs={[
        ...(lms
          ? [
              {
                key: 'my-learning',
                label: 'My Learning',
                icon: GraduationCap,
                render: () => <StaffLearnPage />,
              },
            ]
          : []),
        ...(staffTraining
          ? [
              ...(showMyTrainings
                ? [
                    {
                      key: 'my-trainings',
                      label: 'My Trainings',
                      icon: ClipboardList,
                      render: () => <MyTrainingsPage />,
                    },
                  ]
                : []),
              ...(showMyTeam
                ? [
                    {
                      // TRUS-562: "the managers of those individuals need a place to
                      // view their staff records (in addition to the admin)."
                      //
                      // Beside My Trainings, not in the admin Training hub: it is
                      // the same person's own view one level out. The server derives
                      // the team from the reporting line, so an admin opening it
                      // sees THEIR reports rather than the org — Compliance is the
                      // org view and stays admin-only.
                      //
                      // TRUS-592: switchable per role (Page Access → "Learning — My
                      // Team"), e.g. off for frontline Staff who manage nobody.
                      key: 'my-team',
                      label: 'My Team',
                      icon: Users,
                      render: () => <MyTeamPage />,
                    },
                  ]
                : []),
            ]
          : []),
        ...(lms
          ? [
              {
                key: 'courses',
                label: 'Courses',
                icon: BookOpen,
                render: () => <LmsCoursesAdminPage />,
              },
              {
                key: 'curricula',
                label: 'Curricula',
                icon: Layers,
                render: () => <WorkforceCurricula />,
              },
            ]
          : []),
      ]}
    />
  );
}
